/**
 * ChallengeFormFieldSelectorModal Component
 *
 * Modal for hierarchical field selection from Challenge Forms.
 * Navigation flow: Program → Version → Item → Field
 *
 * Inspired by the ReportBuilder question selection pattern.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LoopIcon from '@mui/icons-material/Loop';
import { useProgramHierarchy } from '@/api/hooks/analytics/useAnalytics';
import type { ChallengeFormFieldSelection, FieldOptionDto } from '@/api/types/dtos/analytics/AnalyticsDto';
import type { Form } from '@/api/types/forms';
import { buildFieldKey, buildFieldLabel, parseFieldKey } from '../utils/fieldParser';

interface ChallengeFormFieldSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onFieldSelected: (field: ChallengeFormFieldSelection) => void;
  organizationId: string;
  existingFieldKey?: string; // For editing existing graphs
  filterByType?: string[]; // Optional filter to only show questions of certain types (e.g., ['date', 'datetime'])
  title?: string; // Optional custom title for the modal
}

interface SelectionState {
  programId: string | null;
  versionId: string | null;
  itemId: string | null;
  questionId: string | null;
}

interface ForEachContextOption {
  value: string;
  label: string;
}

export const ChallengeFormFieldSelectorModal: React.FC<ChallengeFormFieldSelectorModalProps> = ({
  open,
  onClose,
  onFieldSelected,

  existingFieldKey,
  filterByType,
  title = 'Select Field from Challenge Form',
}) => {
  // Selection state
  const [selection, setSelection] = useState<SelectionState>({
    programId: null,
    versionId: null,
    itemId: null,
    questionId: null,
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // FOR_EACH context selection state
  const [selectedForEachContexts, setSelectedForEachContexts] = useState<string[]>([]);

  // Expanded accordions state
  const [expandedLevelIds, setExpandedLevelIds] = useState<Set<string>>(new Set());

  // Use the hierarchy endpoint which returns pre-parsed fields
  const { data: hierarchy = [], isLoading: hierarchyLoading } = useProgramHierarchy();

  const displayHierarchy = hierarchy ?? [];

  // Get selected entities
  const selectedProgram = displayHierarchy.find((p) => p.id === selection.programId);
  const selectedVersion = selectedProgram?.versions?.find((v) => v.id === selection.versionId);
  const selectedLevel = selectedVersion?.levels?.find((l) => l.id === selection.itemId || l.items?.some((i) => i.id === selection.itemId));
  const selectedItem = selection.itemId
    ? selectedLevel?.items?.find((i) => i.id === selection.itemId) || selectedVersion?.levels?.flatMap((l) => l.items || []).find((i) => i?.id === selection.itemId)
    : null;

  // Use pre-parsed fields from hierarchy endpoint, or parse from template as fallback
  const availableFields = useMemo(() => {
    if (!selectedItem) return [];

    let fields: any[] = [];

    // Use pre-parsed fields from hierarchy endpoint (primary)
    if (selectedItem.fields && selectedItem.fields.length > 0) {
      fields = selectedItem.fields.map((f: FieldOptionDto) => ({
        ...f,
        // Backend uses 'key' as the questionId equivalent
        questionId: f.key,
      }));
    }

    // Fallback: parse from measurementTemplate for backward compatibility
    if (fields.length === 0 && selectedItem.measurementTemplate) {
      try {
        const parsed = JSON.parse(selectedItem.measurementTemplate) as Form;
        fields = (parsed.questions || []).map((q) => ({
          key: q.id,
          questionId: q.id,
          label: q.text || q.id,
          type: q.type,
        }));
      } catch {
        fields = [];
      }
    }

    // Apply type filter if specified
    if (filterByType && filterByType.length > 0) {
      fields = fields.filter((f) => filterByType.includes(f.type));
    }

    return fields;
  }, [selectedItem, filterByType]);

  // Get selected question with full question data
  const selectedQuestion = availableFields.find((f) => f.questionId === selection.questionId);

  // Parse form definition to get FOR_EACH context options
  const formDefinition = useMemo(() => {
    if (!selectedItem?.measurementTemplate) return null;
    try {
      return JSON.parse(selectedItem.measurementTemplate) as Form;
    } catch (e) {
      console.error('Failed to parse form definition:', e);
      return null;
    }
  }, [selectedItem?.measurementTemplate]);

  // Check if selected question has FOR_EACH display logic
  const isForEachQuestion = useMemo(() => {
    if (!selectedQuestion || !formDefinition) return false;
    const question = formDefinition.questions.find(q => q.id === selectedQuestion.questionId);
    return question?.display_logic?.condition_type === 'for_each';
  }, [selectedQuestion, formDefinition]);

  // Get FOR_EACH context options (from source multiselect question)
  const forEachContextOptions = useMemo(() => {
    if (!selectedQuestion || !formDefinition) return [];
    const question = formDefinition.questions.find(q => q.id === selectedQuestion.questionId);
    if (!question?.display_logic?.source_multiselect_id) return [];

    const sourceQuestion = formDefinition.questions.find(
      q => q.id === question.display_logic.source_multiselect_id
    );
    if (!sourceQuestion?.options) return [];

    return sourceQuestion.options.map(opt => ({
      value: opt.value,
      label: opt.label
    }));
  }, [selectedQuestion, formDefinition]);

  // Get source question name for FOR_EACH display
  const forEachSourceQuestionName = useMemo(() => {
    if (!selectedQuestion || !formDefinition) return null;
    const question = formDefinition.questions.find(q => q.id === selectedQuestion.questionId);
    if (!question?.display_logic?.source_multiselect_id) return null;

    const sourceQuestion = formDefinition.questions.find(
      q => q.id === question.display_logic.source_multiselect_id
    );
    return sourceQuestion?.text || null;
  }, [selectedQuestion, formDefinition]);

  // Reset FOR_EACH selection when question changes
  React.useEffect(() => {
    if (isForEachQuestion) {
      // If editing, load existing forEach values from the key
      if (existingFieldKey) {
        const parsed = parseFieldKey(existingFieldKey);
        if (parsed?.forEachValues) {
          setSelectedForEachContexts(parsed.forEachValues);
        } else {
          setSelectedForEachContexts([]);
        }
      } else {
        setSelectedForEachContexts([]);
      }
    } else {
      setSelectedForEachContexts([]);
    }
  }, [selection.questionId, isForEachQuestion, existingFieldKey]);

  // Filter functions
  const filteredPrograms = displayHierarchy.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredVersions = selectedProgram?.versions?.filter((v) =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredLevels = selectedVersion?.levels?.filter((l) =>
    l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.items?.some((i) => i.name.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];

  // Toggle accordion expansion
  const toggleLevelExpansion = (levelId: string) => {
    setExpandedLevelIds((prev) => {
      const next = new Set(prev);
      if (next.has(levelId)) {
        next.delete(levelId);
      } else {
        next.add(levelId);
      }
      return next;
    });
  };

  // Get items for a specific level (with search filter applied)
  const getLevelItems = (level: any) => {
    if (!level || !level.items) return [];
    if (!searchQuery) return level.items;
    return level.items.filter((i: any) =>
      i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (i.cpNumber && i.cpNumber.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  // Handlers
  const handleProgramChange = (event: any) => {
    const programId = event.target.value;
    setSelection({
      programId,
      versionId: null,
      itemId: null,
      questionId: null,
    });
    setExpandedLevelIds(new Set());
    setSearchQuery('');
  };

  const handleVersionChange = (event: any) => {
    const versionId = event.target.value;
    setSelection((prev) => ({
      ...prev,
      versionId,
      itemId: null,
      questionId: null,
    }));

    // Auto-expand the first level of the selected version
    const version = displayHierarchy
      .find((p) => p.id === selection.programId)
      ?.versions?.find((v) => v.id === versionId);
    if (version && version.levels && version.levels.length > 0) {
      setExpandedLevelIds(new Set([version.levels[0].id]));
    } else {
      setExpandedLevelIds(new Set());
    }
    setSearchQuery('');
  };

  const handleItemClick = (itemId: string) => {
    setSelection((prev) => ({
      ...prev,
      itemId,
      questionId: null,
    }));
  };

  const handleQuestionSelect = (questionId: string) => {
    setSelection((prev) => ({
      ...prev,
      questionId,
    }));
  };

  const handleConfirm = () => {
    if (!selectedProgram || !selectedVersion || !selectedItem || !selectedQuestion) {
      return;
    }

    const fieldKey = buildFieldKey(
      selectedQuestion.questionId,  // Just question UUID
      isForEachQuestion ? selectedForEachContexts : undefined
    );

    const itemName = selectedItem.cpNumber
      ? `${selectedItem.cpNumber}: ${selectedItem.name}`
      : selectedItem.name;

    const fieldSelection: ChallengeFormFieldSelection = {
      key: fieldKey,
      label: buildFieldLabel(selectedProgram.name, selectedVersion.name, itemName, selectedQuestion.label),
      type: selectedQuestion.type,
      questionId: selectedQuestion.questionId,  // Just question UUID
      forEachContext: isForEachQuestion ? selectedForEachContexts : undefined,
    };

    onFieldSelected(fieldSelection);
  };

  const canConfirm = !!selectedProgram && !!selectedVersion && !!selectedItem && !!selectedQuestion &&
    (!isForEachQuestion || selectedForEachContexts.length > 0);

  // Build preview label
  const getPreviewLabel = () => {
    if (!selectedProgram) return 'Select a program...';
    if (!selectedVersion) return `${selectedProgram.name} / Select a version...`;
    if (!selectedItem) return `${selectedProgram.name} / ${selectedVersion.name} / Select an item...`;
    if (!selectedQuestion) return `${selectedProgram.name} / ${selectedVersion.name} / ${selectedItem.name} / Select a field...`;

    const baseLabel = buildFieldLabel(
      selectedProgram.name,
      selectedVersion.name,
      selectedItem.cpNumber ? `${selectedItem.cpNumber}: ${selectedItem.name}` : selectedItem.name,
      selectedQuestion.label
    );

    if (isForEachQuestion && selectedForEachContexts.length > 0) {
      return `${baseLabel} (FOR_EACH: ${selectedForEachContexts.join(', ')})`;
    }
    if (isForEachQuestion) {
      return `${baseLabel} (Select FOR_EACH contexts...)`;
    }

    return baseLabel;
  };

  const isLoading = hierarchyLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderOpenIcon />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : displayHierarchy.length === 0 ? (
          <Alert severity="info">
            No programs or forms found. Please create programs with items and forms first.
          </Alert>
        ) : (
          <Box>
            {/* Search */}
            <TextField
              fullWidth
              placeholder="Search programs, versions, items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Program Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Program</InputLabel>
              <Select
                value={selection.programId || ''}
                onChange={handleProgramChange}
                label="Program"
              >
                {filteredPrograms.map((program) => (
                  <MenuItem key={program.id} value={program.id}>
                    {program.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Version Selection */}
            {selectedProgram && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Program Version</InputLabel>
                <Select
                  value={selection.versionId || ''}
                  onChange={handleVersionChange}
                  label="Program Version"
                >
                  {filteredVersions.map((version) => (
                    <MenuItem key={version.id} value={version.id}>
                      {version.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Items by Level */}
            {selectedVersion && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Items (by Level)
                </Typography>
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {filteredLevels.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No items found for this version
                    </Typography>
                  ) : (
                    filteredLevels.map((level) => {
                      const levelItems = getLevelItems(level);
                      const isExpanded = expandedLevelIds.has(level.id);
                      return (
                        <Accordion
                          key={level.id}
                          expanded={isExpanded}
                          onChange={() => toggleLevelExpansion(level.id)}
                          sx={{ mb: 1 }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Level: {level.name} ({level.items.length} items)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {levelItems.length === 0 ? (
                              <Typography variant="body2" color="textSecondary">
                                No items in this level
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {levelItems.map((item: any) => {
                                  const itemLabel = item.cpNumber
                                    ? `${item.cpNumber}: ${item.name}`
                                    : item.name;
                                  return (
                                    <Paper
                                      key={item.id}
                                      variant="outlined"
                                      sx={{
                                        p: 1,
                                        cursor: 'pointer',
                                        borderColor: selection.itemId === item.id ? 'primary.main' : 'divider',
                                        bgcolor: selection.itemId === item.id ? 'action.selected' : 'background.paper',
                                      }}
                                      onClick={() => handleItemClick(item.id)}
                                    >
                                      <Typography variant="body2">{itemLabel}</Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {item.measurementType}
                                      </Typography>
                                    </Paper>
                                  );
                                })}
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Box>
              </Box>
            )}

            {/* Field Selection */}
            {selectedItem && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Field from Form
                </Typography>
                <RadioGroup
                  value={selection.questionId || ''}
                  onChange={(e) => handleQuestionSelect(e.target.value)}
                >
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 250, overflowY: 'auto' }}>
                    {availableFields.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No fields found in form definition
                      </Typography>
                    ) : (
                      availableFields.map((field) => {
                        // Check if this field has FOR_EACH display logic
                        const isFieldForEach = formDefinition?.questions.find(
                          q => q.id === field.questionId
                        )?.display_logic?.condition_type === 'for_each';

                        return (
                          <FormControlLabel
                            key={field.questionId}
                            value={field.questionId}
                            control={<Radio />}
                            label={
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2">{field.label}</Typography>
                                  {isFieldForEach && (
                                    <LoopIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <Chip
                                    label={field.type}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: 10, height: 20 }}
                                  />
                                  {field.enumValues && field.enumValues.length > 0 && (
                                    <Typography variant="caption" color="textSecondary">
                                      ({field.enumValues.slice(0, 3).join(', ')}
                                      {field.enumValues.length > 3 ? '...' : ''})
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            }
                            sx={{ mb: 1 }}
                          />
                        );
                      })
                    )}
                  </Paper>
                </RadioGroup>
              </Box>
            )}

            {/* FOR_EACH Context Selection */}
            {isForEachQuestion && forEachContextOptions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LoopIcon fontSize="small" color="primary" />
                    FOR_EACH Context Selection
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This question is repeated for each option selected in{' '}
                    <strong>&quot;{forEachSourceQuestionName || 'the source question'}&quot;</strong>.
                    Select which instances to include in the graph:
                  </Alert>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                    Available options ({selectedForEachContexts.length} selected):
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                    {forEachContextOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={selectedForEachContexts.includes(option.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedForEachContexts(prev => [...prev, option.value]);
                              } else {
                                setSelectedForEachContexts(prev => prev.filter(v => v !== option.value));
                              }
                            }}
                          />
                        }
                        label={option.label}
                        sx={{ display: 'flex', mr: 0 }}
                      />
                    ))}
                  </Box>
                  {selectedForEachContexts.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Please select at least one option to continue
                    </Alert>
                  )}
                  {selectedForEachContexts.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="primary">
                        Selected: {selectedForEachContexts.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* Preview */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                Selected Field:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getPreviewLabel()}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          startIcon={<FolderOpenIcon />}
        >
          Select Field
        </Button>
      </DialogActions>
    </Dialog>
  );
};
